import { inject, Injectable } from '@angular/core';
import { BindWidgetsVariablesUtilsService, PrefabService, ProjectService, WmHttpService } from '@wms/app/services';
import { BaseVariableService } from '@wms/app/variables';
import { PageWsService, WmsWorkspaceManagerService } from '@wms/app/workspace';
import { find, forEach, forOwn, uniqWith } from 'lodash-es';
import { getPageWidgetRegistry } from "@wm/components-base";
import { environment } from '@wms/app';
import { IPrefabObj, IwidgetsForPrefab } from './interface/interface-for-intellisense';
import * as monaco from 'monaco-editor';
type MonacoInstance = typeof monaco;

@Injectable({
    providedIn: 'root'
})
export class IntellisenseGeneratorService {
    private readonly TARGET = 'Monaco';
    private readonly API_META = {
        getIntellisenseJson: {
            method: "GET",
            spinnerInfo: {
                showSpinner: false,
            },
        },
    };
    private activeResourcePath :string = "";
    private  isFileWorkSpace: boolean = false;
    private baseVariableService: BaseVariableService
    private pageWsService: PageWsService;
    private nameDifferenceMap = new Map<string, string>([
        ['Datetime', 'DateTime'],
        ['Listtemplate', 'ListTemplate'],
        ["Layoutgrid", "LayoutGrid"],
        ["Gridrow", "LayoutGridRow"],
        ["Gridcolumn", "LayoutGridColumn"],
        ["Buttongroup", "ButtonGroup"],
        ["TimerVariable", "TimerAction"],
        ["NavigationVariable", "NavigationAction"],
        ["LoginVariable", "LoginAction"],
        ["LogoutVariable", "LogoutAction"],
        ["NotificationVariable", "NotificationAction"],
        ["Variable", "ModelVariable"],
        ["Richtexteditor", "RichTextEditor"],
        ["Livefilter", "LiveFilter"],
        ["Barcodescanner", "BarcodeScanner"],
        ["Tabpane", "TabPane"],
        ["Alertdialog", "AlertDialog"],
        ["Confirmdialog", "ConfirmDialog"],
        ["Logindialog", "LoginDialog"],
        ["Iframedialog", "IframeDialog"],
        ["Text", "InputText"]
    ])
    private actionCategories = ['wm.NavigationVariable', 'wm.LoginVariable', 'wm.LogoutVariable', 'wm.TimerVariable', 'wm.NotificationVariable']
    private variableCategories = ["wm.LiveVariable", "wm.CrudVariable", "wm.ServiceVariable", "wm.WebSocketVariable", "wm.Variable", "wm.DeviceVariable"]
    private completeDeclarationString: string;
    private widgetsForPrefab: IwidgetsForPrefab = {
        widgetsContainPrefab: false,
        prefabArray: []
    };
    private activePageName: string;

    constructor(
        private readonly wmHttpService: WmHttpService,
        private projectService: ProjectService,
        private prefabService: PrefabService,
        private wsManagerService: WmsWorkspaceManagerService,
        private bindwidgetVariableUtilsService: BindWidgetsVariablesUtilsService
    ) {
        this.wmHttpService.registerApiMeta(this.TARGET, this.API_META);
        this.pageWsService = inject(PageWsService);
        this.baseVariableService = (this.pageWsService as any).baseVariableService;
    }

    private async getConfigJSON(url: string): Promise<IPrefabObj> {
        try {
            return await this.wmHttpService.execute({
                target: this.TARGET,
                url,
                action: 'getIntellisenseJson',
                urlParams: {
                    projectID: this.projectService.getProjectId(),
                },
            }) as Promise<any>;
        } catch (error) {
            console.error('Error fetching the JSON configuration:', error);
            throw error;
        }
    }
    /**
     * The function `addJSSuggestions` asynchronously retrieves and organizes widget and variable
     * instances to be added to a specified section in a string.
     * @param {string} declarationContentString - The `addJSSuggestions` function takes a `copyWidgets` string as a
    parameter and performs the following tasks:
     * @returns The `addJSSuggestions` function returns a Promise that resolves to a string. The string
    contains the modified `copyWidgets` content with added lines for widget instances, page variable
    instances, app variable instances, page action instances, and app action instances.
     */
    public async addJSSuggestions(monaco: MonacoInstance, declarationContentString: string): Promise<monaco.IDisposable> {
        this.widgetsForPrefab.prefabArray = []
        this.widgetsForPrefab.widgetsContainPrefab = false
        this.completeDeclarationString = declarationContentString
        this.setActivePageName();
        //below regex checks whether the .js file is present under pages
        const regex = new RegExp(`src/main/webapp/pages/[^/]+/${this.activePageName}`);
        if(!this.isFileWorkSpace || regex.test(this.activeResourcePath)) {
          await this.getWidgets();
          await this.getVariables()
          await this.getPrefabData()
        }
        return monaco.languages.typescript.javascriptDefaults.addExtraLib(this.completeDeclarationString)
    }

    setActivePageName() {
        if (this.wsManagerService.getActiveWSCategory() === 'PAGE') {
            this.activePageName = this.pageWsService.activePageName;
        }
        else if (this.wsManagerService.getActiveWSCategory() === 'RESOURCE') {
            let fileName = this.wsManagerService.getFileRef().selectedNode.name;
            // Split the string by dots and take the first part -ex: Main.js.
            this.activePageName = fileName.split('.')[0];;
        }
    }

    /**
     * The function categorizes variables based on their category and stores them in separate
     * arrays.
     * @param {any} data - The `data` parameter in the `categorizeVariables` function is
     * expected to be an object containing information about variables. Each entry in the
     * `data` object should have a `name` property (string) and a `category` property (string)
     * that specifies the category of the variable.
     * @param {string[]} actionArray - An array that will store the names of variables
     * categorized as actions.
     * @param {string[]} variableArray - The `variableArray` parameter is an array that is used
     * to store variables along with their corresponding component names.
     */
    private categorizeVariables(data: any, actionArray: string[], variableArray: string[]) {
        forOwn(data, (value: { name: string, category: string }) => {
            let componentName = value.category.replace('wm.', '')
            if (this.nameDifferenceMap.has(componentName))
                componentName = this.nameDifferenceMap.get(componentName) as string
            if (this.actionCategories.includes(value.category))
                actionArray.push(value.name + ' : ' + `${componentName}`)
            else if (this.variableCategories.includes(value.category))
                variableArray.push(value.name + ' : ' + `${componentName}`)
        })
    }
    /**
     * Adds new lines at the specified section in the copyWidgets string.
     * @param sectionMarker The marker to locate where to insert new lines.
     * @param newLines The lines to be added.
     */
    private addData(sectionMarker: string, newLines: string[]) {
        // Split the string into an array of lines
        const lines = this.completeDeclarationString.split('\n');
        // Find the index of the line with the closing bracket
        const instanceOf = lines.findIndex(line => line.includes(sectionMarker));
        // Insert new lines before the closing bracket
        lines.splice(instanceOf + 1, 0, ...newLines);
        // Join the lines back into a string
        const modifiedString = lines.join('\n');
        this.completeDeclarationString = modifiedString
    }
    private async getWidgets() {
        let widgets: any;
        widgets = getPageWidgetRegistry(this.activePageName);
        if (!widgets) { 
            const url = environment.SERVICES_BASE_PATH + `services/projects/${this.projectService.getProjectId()}/pages/${this.activePageName}/page.min.json`;
            const data: any = await this.bindwidgetVariableUtilsService.getPageData(url, this.activePageName, "0");
            (this.bindwidgetVariableUtilsService as any).unRegisterWidgets();
            (this.bindwidgetVariableUtilsService as any).registerWidgets(data.widgets, this.activePageName);
            widgets = getPageWidgetRegistry(this.activePageName);
        }
        const widgetsArray: string[] = []
        forEach(Object.fromEntries(widgets), (w: { name: string, widgetType: string, prefabName?: string }) => {
            // Remove 'wm-' prefix, split by hyphens, capitalize each word, join them, and append "Component"
            let componentName = w.widgetType.replace('wm-', '')
                .split('-')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join('')
            if (w.widgetType === 'wm-prefab') {
                this.widgetsForPrefab.widgetsContainPrefab = true
                this.widgetsForPrefab.prefabArray.push(w.prefabName as string)
                widgetsArray.push(w.name + " : " + w.prefabName)
                return
            }
            if (this.nameDifferenceMap.has(componentName))
                componentName = this.nameDifferenceMap.get(componentName) as string
            if (this.completeDeclarationString.includes(`${componentName}Component`))
                componentName = `${componentName}Component`
            else if (this.completeDeclarationString.includes(`${componentName}Directive`))
                componentName = `${componentName}Directive`
            widgetsArray.push(w.name + ' : ' + `${componentName}`)
        })
        this.addData('/** LIST OF PAGEWIDGET INSTANCES **/', widgetsArray)
    }
    private async getVariables(): Promise<void> {
        const pageVariables = await this.baseVariableService.getPageVariables(this.activePageName)
        const appVariables = await this.baseVariableService.getAppVariables()
        const pageVariablesarray: string[] = [],
            appVariablesarray: string[] = [],
            pageActionsarray: string[] = [],
            appActionsarray: string[] = []
        this.categorizeVariables(pageVariables, pageActionsarray, pageVariablesarray)
        this.categorizeVariables(appVariables[0], appActionsarray, appVariablesarray)
        this.addData('/** LIST OF PAGEVARIABLE INSTANCES **/', pageVariablesarray)
        this.addData('/** LIST OF APPVARIABLE INSTANCES **/', appVariablesarray)
        this.addData('/** LIST OF PAGEACTION INSTANCES **/', pageActionsarray)
        this.addData('/** LIST OF APPACTION INSTANCES **/', appActionsarray)
    }
    private async getPrefabData() {
        if (this.projectService.isPrefabType()) {
            const prefabData = await this.getConfigJSON(`${environment.SERVICES_BASE_PATH}services/projects/:projectID/resources/content/project/src/main/webapp/config.json`)
            this.addData('//** LIST OF PREFAB INSTANCES **/', this.createPrefabClassString(prefabData, 'const Prefab: PrefabComponent;\n\n declare class PrefabComponent {\n',));
        }
        if (this.widgetsForPrefab.widgetsContainPrefab) {
            const prefabConfigs: { [key: string]: IPrefabObj } = this.prefabService.getPrefabConfig()
            forEach(this.widgetsForPrefab.prefabArray, (prefabName) => {
                const prefabData = find(prefabConfigs, { displayName: prefabName });
                if (prefabData) {
                    this.addData('//** LIST OF PREFAB INSTANCES **/', this.createPrefabClassString(prefabData, `declare class ${prefabData.displayName} {\n`));
                }
            });
        }
    }
    private createPrefabClassString(prefabObj: IPrefabObj, classStr: string): string[] {
        // Add properties only if there is more than one property
        if (prefabObj?.properties) {
            const propertyEntries = Object.entries(prefabObj?.properties);
            for (const [propName, { description, type }] of propertyEntries) {
                const formattedDescription = description.split('\n').map(line => `     * ${line}`).join('\n');
                classStr += `    /**\n${formattedDescription}\n     */\n`;
                classStr += `    ${propName}: ${type};\n\n`;
            }
        }
        // Add methods only if there is more than one method
        if (prefabObj?.methods) {
            const methodEntries = Object.entries(prefabObj?.methods);
            for (const [methodName, { description, returnType, parameters }] of methodEntries) {
                const formattedDescription = description.split('\n').map(line => `     * ${line}`).join('\n');
                classStr += `    /**\n${formattedDescription}\n`;
                // Add parameters with their JSDoc tags
                parameters.forEach(({ name, type, description }) => {
                    const formattedParamDescription = description.split('\n').map(line => ` ${line}`).join('\n');
                    classStr += `     * @param {${type}} ${name} ${formattedParamDescription}\n`;
                });
                // Add return type
                classStr += `     * @returns {${returnType}}\n     */\n`;
                // Build the method signature
                const paramsStr = parameters.map(({ name, type }) => `${name}: ${type}`).join(', ');
                classStr += `    ${methodName}(${paramsStr}): ${returnType};\n\n`;
            }
        }
        classStr += '}\n';
        return classStr.split('\n');
    }

   /**
    * The function `addCssSuggestions` asynchronously registers a completion item provider for CSS
    * suggestions in a Monaco editor for native mobile applications.
    * @param {MonacoInstance} monaco - The `monaco` parameter in the `addCssSuggestions` function is an
    * instance of the Monaco Editor. Monaco Editor is a browser-based code editor that powers Visual
    * Studio Code's editor as well as other online code editors.
    * @returns The `addCssSuggestions` function returns a Promise that resolves to either `void` or a
    * `monaco.IDisposable` object. The function registers a completion item provider for CSS language
    * in Monaco editor when the project type is a native mobile application. The completion item
    * provider provides suggestions for CSS classes based on a JSON file containing metadata.
    */
    public async addCssSuggestions(monaco: MonacoInstance): Promise<void | monaco.IDisposable> {
        if (this.projectService.isNativeMobileApplicationType()) {
            return monaco.languages.registerCompletionItemProvider('css', {
                triggerCharacters: ['.'], // Specify characters that trigger suggestions
                provideCompletionItems: async (model: any, position: any) => {
                    const word = model.getWordUntilPosition(position);
                    const range = {
                        startLineNumber: position.lineNumber,
                        startColumn: word.startColumn,
                        endLineNumber: position.lineNumber,
                        endColumn: word.endColumn
                    };
                    //@ts-ignore
                    const response = await import('../../native-mobile/native-mobile-css-metadata.json');
                    const classes = Object.keys(response.default).map(name => ({
                        label: name,
                        documentation: 'Represents the css class selector',
                        kind: 18, // equivalent to monaco.languages.CompletionItemKind.Text
                        insertText: name,
                        sortText: name,
                        range: range
                    }))
                    return {
                        suggestions: classes
                    }
                }
            });
        }
    }

    public async addSqlSuggestions(monaco: MonacoInstance): Promise<void | monaco.IDisposable> {
            return monaco.languages.registerCompletionItemProvider('sql', {
                provideCompletionItems: async (model: any, position: any) => {
                    const word = model.getWordUntilPosition(position);
                    const range = {
                        startLineNumber: position.lineNumber,
                        startColumn: word.startColumn,
                        endLineNumber: position.lineNumber,
                        endColumn: word.endColumn
                    };
                    const defaultKeyword = this.getDefaultSqlSuggestion()
                    let allSuggestions = this.projectService.sqlSuggestions ? this.projectService.sqlSuggestions.concat(defaultKeyword) : defaultKeyword
                    const classes = (allSuggestions).map((data: any) => ({
                        label: data.name,
                        kind: 17, // equivalent to monaco.languages.CompletionItemKind.keyword
                        insertText: data.name,
                        sortText: data.name,
                        detail:data.meta,
                        range: range
                    }))
                    return {
                        suggestions: this.removeDuplicatesByLabelAndKind(classes)
                    }
                }
            });
    }

    public async addMySqlSuggestions(monaco: MonacoInstance): Promise<void | monaco.IDisposable> {
        return monaco.languages.registerCompletionItemProvider('mysql', {
            provideCompletionItems: async (model: any, position: any) => {
                const word = model.getWordUntilPosition(position);
                const range = {
                    startLineNumber: position.lineNumber,
                    startColumn: word.startColumn,
                    endLineNumber: position.lineNumber,
                    endColumn: word.endColumn
                };
                const classes = (this.projectService.mysqlSuggestions).map((data: any) => ({
                    label: data.caption,
                    kind: 17, // equivalent to monaco.languages.CompletionItemKind.keyword
                    insertText: data.value,
                    sortText: data.caption,
                    range: range
                }))
                return {
                    suggestions: this.removeDuplicatesByLabelAndKind(classes)
                }
            }
        });
}

    removeDuplicatesByLabelAndKind(suggestions: monaco.languages.CompletionItem[]): monaco.languages.CompletionItem[] {
        return uniqWith(suggestions, (a, b) => {
          return a.label === b.label && a.detail === b.detail;
        });
      }

      getDefaultSqlSuggestion(){
        const defaultKeywords = [
            "select", "insert", "update", "delete", "from", "where", "and", "or", 
            "group", "by", "order", "limit", "offset", "having", "as", "case", 
            "when", "else", "end", "type", "left", "right", "join", "on", "outer", 
            "desc", "asc", "union", "create", "table", "primary", "key", "if", 
            "foreign", "not", "references", "default", "null", "inner", "cross", 
            "natural", "database", "drop", "grant", "avg", "count", "first", 
            "last", "max", "min", "sum", "ucase", "lcase", "mid", "len", "round", 
            "rank", "now", "format", "coalesce", "ifnull", "isnull", "nvl", 
            "int", "numeric", "decimal", "date", "varchar", "char", "bigint", 
            "float", "double", "bit", "binary", "text", "set", "timestamp", 
            "money", "real", "number", "integer"
          ]
          let completionList:any = []
          defaultKeywords.forEach((keyword) => {
            completionList.push({
                'name': keyword,
                'value': keyword,
                'meta': 'keyword'
            });
        })
        return completionList;
      }
      public setActiveResourcePath(activeResourcePath: string){
        this.activeResourcePath = activeResourcePath;
      }

      public setIsFileWorkSpace(value: boolean){
       this.isFileWorkSpace = value;
      }
}
